// src/encounter/vaccination-admin-editor.tsx
// VaccinationAdminEditor — the shared vaccine administration popup for the encounter A&P + Pre-encounter
// surfaces (lot-aware deduction, PROD-1227). React port of Flutter VaccinationAddUpdateWidget
// (encounter_assessment_plan/.../vaccination_add_widget.dart), one component for both surfaces via
// `isAdministration` (pre-encounter = nurse administering → lot/expiry/etc. mandatory; A&P plan =
// doctor requesting → "Administration request for nurse" toggle).
//
// Field order follows the APPROVED design reorder (Samer 2026-06-18), an intentional divergence from
// Flutter: Vaccination date · Site / Expiration date · Lot no. The Lot no. field is the LotPicker
// (lot-aware): pick an on-hand lot (auto-fills expiry) or go off-system (free text + caution banner).
// Route + admin/dose Unit derive from the selected vaccine (SearchValueSets2 carries route[] +
// unitOfMeasurement). The picker READS inventory only — deduction is server-side on sign-lock.
//
// NOTE: the save wire (POST /api/Patients/CreateAssessment) is owned by the caller via `onSave`;
// the assembled VaccinationDraft is curl-shaped in use-create-assessment.ts (PROD-1227 #32).
import { useMemo, useState } from 'react';
import { useDesignV2 } from './lib/design-v2';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Banner, Button, LotPicker, NumberSelector, Select, Switch, TextArea, TextField } from '@scripsteam/scrips-signal-ds';
import { AlertTriangle, ChevronLeft, Trash2 } from 'lucide-react';
import { AP_DIALOG_LABELS } from './ap-dialog-label-style';
import { FormDatePicker } from '@/shared/components/form-date-picker';
import { dateToYmd, isoToYmdLocal } from '@/shared/lib/local-date';
import { useLotsByVaccineCode } from './services/use-item-lots';
import {
  useAdministrationSiteList,
  useMedicationUnitOfMeasurement,
  useVaccinationDurationQuantity,
  useVaccinationDurationUnits,
  useVaccinationPattern,
} from '@/shared/services/use-vaccine-master-lookups';
import type { VaccineValueSet } from '@/shared/services/use-vaccination-search';

// ── Coding shape (NAB value-sets ship {code, display, system}) ───────────────────────────────────
export interface Coding {
  code: string;
  display: string;
  system: string;
}
/** Assembled administration draft handed to onSave; the save hook curl-shapes it for CreateAssessment. */
export interface VaccinationDraft {
  vaccine: VaccineValueSet;
  administratedDate: string | null; // no-Z ISO
  administratedDisplay: string;      // YYYY-MM-DD (input value)
  lotNo: string;
  isOffSystemLot: boolean;
  expiryDate: string | null;         // no-Z ISO
  expiryDisplay: string;             // YYYY-MM-DD
  site: Coding | null;
  route: Coding | null;
  repeatPattern: Coding | null;
  administrationAmount: number | null;
  administrationUnit: string | null;
  administrationDoseQuantity: number | null;
  administrationDoseUnit: string | null;
  durationQuantity: Coding | null;
  durationUnits: Coding | null;
  remarks: string;
  isAdministrationRequested: boolean;
}

/** form YYYY-MM-DD → no-Z ISO (`YYYY-MM-DDT00:00:00.000`). .NET model binder rejects the Z suffix. */
function toNoZIso(ymd: string): string | null {
  if (!ymd) return null;
  return `${ymd}T00:00:00.000`;
}
/** no-Z ISO (or any ISO) → LOCAL YYYY-MM-DD for the form field. The previous
 *  `toISOString().slice(0,10)` here converted local midnight → UTC and wrote YESTERDAY's date
 *  in +tz zones (the PROD-1489 Resolve-date bug class) — isoToYmdLocal stays on the local calendar. */
const toYmd = isoToYmdLocal;

export interface VaccinationAdminEditorProps {
  /** Pre-selected vaccine (header) — provides the DDC code (→ lots), route[] and unitOfMeasurement. */
  vaccine: VaccineValueSet;
  /** true = nurse administering (lot/expiry/site/route/etc. mandatory); false = doctor requesting. */
  isAdministration: boolean;
  initial?: Partial<VaccinationDraft>;
  saving?: boolean;
  /** "Save & sign" on the pre-encounter administration; "Save" on the A&P plan. */
  saveLabel?: string;
  /**
   * Form-level failure from the host's save (a failed PUT), rendered above the actions. Distinct from
   * the per-field `error` on Field: this is "the write did not happen", and it must be visible because
   * a silently-failed administration save leaves the nurse believing the dose was recorded.
   */
  saveError?: string;
  /**
   * Rendered directly BELOW the header and above the fields — where the diagnosis block belongs.
   * Flutter's shared add widget builds header() then diagnosisSelectionWidget()
   * (base_ap_item_add_widget.dart:192,195), and our other five order dialogs do the same: the item
   * you are ordering, then the reason. The vaccine dialog had the diagnosis ABOVE the vaccine, which
   * made it the only inverted surface in the set.
   */
  headerSlot?: React.ReactNode;
  onSave: (draft: VaccinationDraft) => void;
  onCancel: () => void;
  /**
   * Destructive DELETE, opposite the actions — the red trash glyph the Figma order-dialog frames carry
   * bottom-right. Editing an existing order only; omitted, nothing renders.
   */
  onDelete?: () => void;
  /**
   * AUTHORING an order set (PROD-2714, Settings › Order sets): no patient and no visit, so the
   * vaccination date, expiration date, lot no. and remarks are not shown and never required (the
   * Save-as board's "Not kept: the patient, dates, notes to the lab, order and lot numbers").
   * Site, route, pattern, amounts and duration stay: they are the set's defaults.
   */
  authoring?: boolean;
}

export function VaccinationAdminEditor({
  vaccine,
  isAdministration,
  initial,
  saving = false,
  saveLabel,
  saveError,
  headerSlot,
  onSave,
  onCancel,
  onDelete,
  authoring = false,
}: VaccinationAdminEditorProps) {
  // Design sheet v1.1, switch ON only (`?ef=frameV2`); OFF renders today's tag and bin.
  const designV2 = useDesignV2();
  const sitesQ = useAdministrationSiteList(true);
  const patternQ = useVaccinationPattern(isAdministration);
  const durQtyQ = useVaccinationDurationQuantity(isAdministration);
  const durUnitsQ = useVaccinationDurationUnits(isAdministration);
  const uomQ = useMedicationUnitOfMeasurement(isAdministration);

  // Lots for THIS vaccine's DDC code (read-only; resolves via Items/ByCode/{code}/Lots).
  const lotsQ = useLotsByVaccineCode(vaccine.valueCoding.code);
  const lots = lotsQ.data ?? [];

  const routes = vaccine.route ?? [];
  const singleRoute = routes.length === 1 ? routes[0] : null;

  // Off-system manual lot entry (the "Lot not listed" branch).
  const [manualLot, setManualLot] = useState(initial?.isOffSystemLot ?? false);

  const schema = useMemo(
    () =>
      z
        .object({
          administratedDisplay: z.string(),
          lotNo: z.string(),
          expiryDisplay: z.string(),
          siteCode: z.string(),
          routeCode: z.string(),
          repeatPatternCode: z.string(),
          administrationAmount: z.string(),
          administrationUnit: z.string(),
          doseQuantity: z.string(),
          doseUnit: z.string(),
          durationQtyCode: z.string(),
          durationUnitsCode: z.string(),
          remarks: z.string().max(300).optional(),
          isAdministrationRequested: z.boolean(),
        })
        .superRefine((v, ctx) => {
          // Flutter requiredness gate (vaccination_add_widget.dart): a field is mandatory when
          //   (isAdministrationRequested == false || isAdministration == true).
          // i.e. always required when actually administering (pre-encounter); in A&P it's required
          // unless the doctor flips "Administration request for nurse" ON (then the nurse fills it).
          if (!(isAdministration || !v.isAdministrationRequested)) return;
          const req = (field: keyof typeof v, path: string) => {
            if (!String(v[field] ?? '').trim())
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: [path] });
          };
          if (!authoring) {
            req('administratedDisplay', 'administratedDisplay');
            req('lotNo', 'lotNo');
            req('expiryDisplay', 'expiryDisplay');
          }
          req('siteCode', 'siteCode');
          if (routes.length > 0) req('routeCode', 'routeCode');
          req('repeatPatternCode', 'repeatPatternCode');
          req('administrationAmount', 'administrationAmount');
          req('administrationUnit', 'administrationUnit');
          req('doseQuantity', 'doseQuantity');
          req('doseUnit', 'doseUnit');
          req('durationQtyCode', 'durationQtyCode');
          req('durationUnitsCode', 'durationUnitsCode');
        }),
    [isAdministration, routes.length, authoring],
  );
  type FormValues = z.infer<typeof schema>;

  // Read once per mount, so a re-render never moves an untouched vaccination date (and never counts
  // as a "defaults changed" reset below).
  const [today] = useState(() => dateToYmd(new Date()));
  const singleRouteCode = singleRoute?.code ?? '';
  const catalogueUnit = vaccine.unitOfMeasurement ?? '';

  // The form's defaults, derived from props on every render (PROD-2912). The vaccine's route and unit
  // can arrive AFTER this editor mounts: a favourite opens with a bare coding and the by-code catalogue
  // lookup fills route + unit when it resolves (vaccine-add-dialog, PROD-2910). `defaultValues` is read
  // once at mount, so the late unit showed in the field labels while both unit dropdowns stayed empty,
  // and the fixed single route never reached `routeCode` (its Required check failed on a filled field).
  const formDefaults = useMemo<FormValues>(
    () => ({
      // FLUTTER SEEDS THE NEW ENTRY (vaccination_add_widget.dart:104-114): administratedDate =
      // DateTime.now(), administrationAmount = 1, administrationDoseQuantity = 1. React was starting
      // all three empty, so the nurse re-entered today's date on every dose and both counters opened
      // at 0.00 on a form where 0 is never a valid administration.
      // dateToYmd, NOT toYmd(new Date().toISOString()): toISOString is UTC and isoToYmdLocal takes the
      // leading YYYY-MM-DD verbatim, so in Dubai (UTC+4) any dose given after 20:00 local would have
      // been seeded with YESTERDAY's date.
      administratedDisplay: toYmd(initial?.administratedDate) || today,
      lotNo: initial?.lotNo ?? '',
      expiryDisplay: toYmd(initial?.expiryDate),
      siteCode: initial?.site?.code ?? '',
      routeCode: initial?.route?.code ?? singleRouteCode,
      repeatPatternCode: initial?.repeatPattern?.code ?? '',
      administrationAmount: initial?.administrationAmount?.toString() ?? '1',
      administrationUnit: initial?.administrationUnit ?? catalogueUnit,
      doseQuantity: initial?.administrationDoseQuantity?.toString() ?? '1',
      doseUnit: initial?.administrationDoseUnit ?? catalogueUnit,
      durationQtyCode: initial?.durationQuantity?.code ?? '',
      durationUnitsCode: initial?.durationUnits?.code ?? '',
      remarks: initial?.remarks ?? '',
      isAdministrationRequested: initial?.isAdministrationRequested ?? false,
    }),
    [initial, today, singleRouteCode, catalogueUnit],
  );

  // react-hook-form's own "default values changed" API: `values` re-seeds the form when it changes
  // (deep-compared, so a re-render with equal defaults is a no-op), and `keepDirtyValues` re-seeds ONLY
  // the fields the user has not changed. So a late catalogue fills the untouched route and unit
  // dropdowns, a unit the physician already picked is kept, and a saved edit keeps its saved values
  // (they come from `initial`, which outranks the catalogue in formDefaults above). No effect, no
  // setState, no remount.
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: formDefaults,
    resetOptions: { keepDirtyValues: true },
  });

  const sites = sitesQ.data ?? [];
  const patterns = patternQ.data ?? [];
  const durQtys = durQtyQ.data ?? [];
  const durUnits = durUnitsQ.data ?? [];
  const uoms = uomQ.data ?? [];

  const codingFrom = (rows: { code?: string | null; display?: string | null; system?: string | null }[], code: string): Coding | null => {
    const r = rows.find((x) => x.code === code);
    return r ? { code: r.code ?? '', display: r.display ?? '', system: r.system ?? '' } : null;
  };

  const submit = form.handleSubmit((v) => {
    onSave({
      vaccine,
      administratedDate: authoring ? null : toNoZIso(v.administratedDisplay),
      administratedDisplay: authoring ? '' : v.administratedDisplay,
      lotNo: authoring ? '' : v.lotNo.trim(),
      isOffSystemLot: authoring ? false : manualLot,
      expiryDate: authoring ? null : toNoZIso(v.expiryDisplay),
      expiryDisplay: authoring ? '' : v.expiryDisplay,
      site: codingFrom(sites, v.siteCode),
      route: routes.find((r) => r.code === v.routeCode) ?? singleRoute ?? null,
      repeatPattern: codingFrom(patterns, v.repeatPatternCode),
      administrationAmount: v.administrationAmount ? Number(v.administrationAmount) : null,
      administrationUnit: v.administrationUnit || null,
      administrationDoseQuantity: v.doseQuantity ? Number(v.doseQuantity) : null,
      administrationDoseUnit: v.doseUnit || null,
      durationQuantity: codingFrom(durQtys, v.durationQtyCode),
      durationUnits: codingFrom(durUnits, v.durationUnitsCode),
      remarks: authoring ? '' : (v.remarks ?? '').trim(),
      isAdministrationRequested: v.isAdministrationRequested,
    });
  });

  const unitOptions = uoms.map((u) => ({ value: u, label: u }));

  // Flutter requiredness: required when administering, OR (in A&P) when the nurse-request toggle is OFF.
  const administrationRequested = form.watch('isAdministrationRequested');
  const fieldsRequired = isAdministration || !administrationRequested;

  return (
    /*
      NO NESTED CARD (31 Jul). This used to carry its own border + shadow + surface + padding, so inside
      a Modal it rendered as a card within a card — the visible reason this dialog "looks totally
      different from the others". The other five compose OrderDialogShell, which puts its content
      directly on the Modal surface. Same here now.
    */
    <div className={`ap-vax-flutter-sizing ${AP_DIALOG_LABELS}`} data-slot="vaccine-form">
      {/* header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        {/*
          Header anatomy copied from OrderDialogShell, not approximated: a 12px bold uppercase field
          label on --color-text-hint, the item name at 15px, the code at 13px muted. The icon seat and
          the bold 16px title were this dialog's own invention and are what made it read as a different
          component. Flutter's shared add widget uses the same label-then-title shape
          (base_ap_item_add_widget.dart:360-372).
        */}
        <div>
          <span className="text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint" data-part="eyebrow">
            Vaccination
          </span>
          {/* 15px = --text-body-15; the traced 1.432 leading is --leading-reading. */}
          <div className="text-size-body-15 leading-reading text-text-primary mt-2" data-part="name">
            {vaccine.valueCoding.display}
          </div>
          {vaccine.valueCoding.code && (
            <div className="text-size-sm leading-reading text-text-muted" data-part="code">
              {vaccine.valueCoding.code}
            </div>
          )}
        </div>
        {/*
          The "Plan" pill is GONE (Andrew, 31 Jul: "what meant by plan in the top, we should remove it").
          Fair — it was mine, not the source's: Flutter's header is the category eyebrow plus the item
          title (base_ap_item_add_widget.dart:360-372, :419-438) with no mode badge, and "Plan" named an
          internal state rather than anything a clinician would recognise.

          "Recording dose" survives, because that one earns its place: in administration mode the lot
          becomes mandatory and the form is capturing a dose that was actually given, which is worth
          saying out loud. It renders only in that mode.
        */}
        {isAdministration && (
          <span
            data-part="pill"
            className={
              designV2
                ? 'shrink-0 whitespace-nowrap rounded-full bg-grey-100 px-3 py-0.5 text-xs font-semibold text-text-secondary'
                : 'shrink-0 whitespace-nowrap rounded-full bg-blue-100 px-3 py-0.5 text-xs font-semibold text-brand-depth'
            }
          >
            Recording dose
          </span>
        )}
      </div>

      {/*
        FLUTTER SIZING, scoped (31 Jul, Andrew: "take the controls from ds but the sizes from flutter").
        Flutter's FieldAndLabel is 36px (scrips_core .../field_and_label.dart:237, :1381), which the DS
        compact family already matches — only the DatePicker trigger is 32, and this editor has two of
        them (vaccination date, expiry). Raised here so the two dates line up with the selects and the
        counters beside them. Same reasoning and same instruction as the medication dialog; see the long
        note there.

        This is NOT the old blanket override. That one forced everything to !h-10 and fought the DS on
        every control; this raises one outlier to the height Flutter and the DS compact family agree on.
      */}
      <style>{`
        /*
          ONE CONTROL HEIGHT, 36px — Flutter's FieldAndLabel default
          (scrips_core .../field_and_label.dart:237, :1381).

          button.h-8  = the DatePicker triggers (vaccination date, expiry), which ship at 32.
          button.h-10 = the LotPicker trigger, which ships at 40 — so the Expiration-date / Lot-no. ROW
                        had a 36 next to a 40 and sat on two different baselines. Andrew caught it in
                        the live encounter; my earlier rule only normalised h-8, because I fixed the
                        controls I knew were in the row instead of measuring what actually was.
          input       = the manual-lot TextField that replaces the picker on "Lot not listed?", which
                        would otherwise swap in at a third height mid-flow.
        */
        .ap-vax-flutter-sizing button.h-8,
        .ap-vax-flutter-sizing button.h-10 { height: 36px; }
        .ap-vax-flutter-sizing input:not([type='checkbox']):not([type='radio']) { height: 36px; }
      `}</style>

      {headerSlot}

      {/*
        NO forced control heights (PROD-1801). This used to carry
        `[&_[role=combobox]]:!h-10` — an !important override of the DS Select's own height — plus
        `inputClassName="!h-10"` on each TextField. Three problems with that: it was an app-side
        override of a DS component (a regression by the DS-first rule even when it looks right); the
        comment justifying it went stale when PROD-1500 swapped the native date input for the DS
        DatePicker (h-8), leaving 40px controls beside a 32px one — the mismatch Samer flagged on
        2026-07-29; and 40px matches nothing in the design. Figma specifies 56px, which IS the DS
        default, so deleting the overrides is the whole fix.
      */}
      <form onSubmit={submit} className="space-y-4">
        {/* Administration request for nurse — TOP of form (Flutter), A&P/plan mode only. ON ⇒ the
            doctor is only requesting; every field below becomes optional (the nurse completes them
            at administration time). OFF (default) ⇒ all fields required. Hidden when administering. */}
        {!isAdministration && (
          <Controller
            name="isAdministrationRequested"
            control={form.control}
            // A SWITCH, not a checkbox (PROD-1801). Flutter renders this as `iOS/SwitchBar` — a
            // toggle in a bordered row with the label on the left — and React had it as a checkbox.
            // The DS `Switch` requires an explicit aria-label, so the visible text is rendered
            // beside it and repeated as the accessible name.
            render={({ field }) => (
              <div className="flex items-center justify-between rounded-md border border-border-default px-3 py-2" data-part="toggle">
                <span className="text-sm text-text-primary" data-part="toggle-text">
                  Administration request for nurse
                </span>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label="Administration request for nurse"
                />
              </div>
            )}
          />
        )}

        {/* administration block — Flutter renders this FIRST (route → … → duration) */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Route — fixed when the vaccine has a single route, else a dropdown */}
          {/* CONTROLLED FROM MOUNT (PROD-2912): route + both units take `field.value` as-is ('' = none,
              which still shows the placeholder), never `|| undefined`. The Base UI Select under the DS
              Select decides controlled vs uncontrolled on its FIRST render and never switches, so a
              Select that mounted with `undefined` ignored the catalogue's late default. */}
          <Field label="Route" required={fieldsRequired} error={form.formState.errors.routeCode?.message}>
            {singleRoute ? (
              <TextField size="compact" value={singleRoute.display} disabled />
            ) : (
              <Controller
                name="routeCode"
                control={form.control}
                render={({ field }) => (
                  <Select size="compact"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select route"
                    options={routes.map((r) => ({ value: r.code, label: r.display }))}
                  />
                )}
              />
            )}
          </Field>

          <Field label="Repeat pattern" required={fieldsRequired} error={form.formState.errors.repeatPatternCode?.message}>
            <Controller
              name="repeatPatternCode"
              control={form.control}
              render={({ field }) => (
                <Select size="compact"
                  value={field.value || undefined}
                  onChange={field.onChange}
                  disabled={patternQ.isLoading}
                  placeholder="Select pattern"
                  options={patterns.map((p) => ({ value: p.code ?? '', label: p.display ?? p.code ?? '—' }))}
                />
              )}
            />
          </Field>

          <Field label={`Administration amount${vaccine.unitOfMeasurement ? ` (${vaccine.unitOfMeasurement})` : ''}`} required={fieldsRequired} error={form.formState.errors.administrationAmount?.message}>
            {/* A COUNTER, per Figma `Inputs/Web/Counter` and the live Flutter dialog. Was a bare
                number input whose native spinners were stripped by the `spin` class — i.e. the
                affordance the design asks for was actively removed. Samer, 2026-07-29:
                "administration amount and dosage amount should be number picker". */}
            <Controller
              name="administrationAmount"
              control={form.control}
              render={({ field }) => (
                <NumberSelector
                  ariaLabel="Administration amount"
                  mode="decimal"
                  step={0.5}
                  min={0}
                  max={999}
                  decimals={2}
                  unit={vaccine.unitOfMeasurement || undefined}
                  value={Number(field.value) || 0}
                  onChange={(n) => field.onChange(String(n))}
                />
              )}
            />
          </Field>
          <Field label="Administration unit" required={fieldsRequired} error={form.formState.errors.administrationUnit?.message}>
            <Controller
              name="administrationUnit"
              control={form.control}
              render={({ field }) => (
                <Select size="compact" value={field.value} onChange={field.onChange} disabled={uomQ.isLoading}
                  placeholder="Select unit" options={unitOptions} />
              )}
            />
          </Field>

          <Field label="Dosage amount" required={fieldsRequired} error={form.formState.errors.doseQuantity?.message}>
            <Controller
              name="doseQuantity"
              control={form.control}
              render={({ field }) => (
                <NumberSelector
                  ariaLabel="Dosage amount"
                  mode="decimal"
                  step={0.5}
                  min={0}
                  max={999}
                  decimals={2}
                  value={Number(field.value) || 0}
                  onChange={(n) => field.onChange(String(n))}
                />
              )}
            />
          </Field>
          <Field label="Dosage unit" required={fieldsRequired} error={form.formState.errors.doseUnit?.message}>
            <Controller
              name="doseUnit"
              control={form.control}
              render={({ field }) => (
                <Select size="compact" value={field.value} onChange={field.onChange} disabled={uomQ.isLoading}
                  placeholder="Select unit" options={unitOptions} />
              )}
            />
          </Field>

          <Field label="Duration quantity" required={fieldsRequired} error={form.formState.errors.durationQtyCode?.message}>
            <Controller
              name="durationQtyCode"
              control={form.control}
              render={({ field }) => (
                <Select size="compact" value={field.value || undefined} onChange={field.onChange} disabled={durQtyQ.isLoading}
                  placeholder="—" options={durQtys.map((d) => ({ value: d.code ?? '', label: d.display ?? d.code ?? '—' }))} />
              )}
            />
          </Field>
          <Field label="Duration unit" required={fieldsRequired} error={form.formState.errors.durationUnitsCode?.message}>
            <Controller
              name="durationUnitsCode"
              control={form.control}
              render={({ field }) => (
                <Select size="compact" value={field.value || undefined} onChange={field.onChange} disabled={durUnitsQ.isLoading}
                  placeholder="—" options={durUnits.map((d) => ({ value: d.code ?? '', label: d.display ?? d.code ?? '—' }))} />
              )}
            />
          </Field>
        </div>

        {/* date/lot/expiry/site — Flutter renders this AFTER the admin block. Lot no. ↔ Site swapped
            per Andrew (only that swap; everything else keeps Flutter order). */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {!authoring && (
          <Field label="Vaccination date" required={fieldsRequired} error={form.formState.errors.administratedDisplay?.message}>
            {/* DS DatePicker via the FormDatePicker adapter (PROD-1500) — replaces the native
                type="date" input (browser-chrome picker, off-system). Field stays a YYYY-MM-DD string. */}
            <FormDatePicker
              control={form.control}
              name="administratedDisplay"
              invalid={!!form.formState.errors.administratedDisplay}
              placeholder="Select date"
            />
          </Field>
          )}
          <Field label="Site" required={fieldsRequired} error={form.formState.errors.siteCode?.message}>
            <Controller
              name="siteCode"
              control={form.control}
              render={({ field }) => (
                <Select size="compact"
                  value={field.value || undefined}
                  onChange={field.onChange}
                  disabled={sitesQ.isLoading}
                  placeholder={sitesQ.isLoading ? 'Loading…' : 'Select site'}
                  options={sites.map((s) => ({ value: s.code ?? '', label: s.display ?? s.code ?? '—' }))}
                />
              )}
            />
          </Field>
        </div>

        {!authoring && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="Expiration date"
            required={fieldsRequired}
            error={form.formState.errors.expiryDisplay?.message}
            hint={manualLot ? 'Entered manually — no batch to auto-fill from' : 'Auto-filled from selected lot'}
          >
            <FormDatePicker
              control={form.control}
              name="expiryDisplay"
              invalid={!!form.formState.errors.expiryDisplay}
              placeholder="Select date"
            />
          </Field>

          <Field
            label="Lot no."
            required={fieldsRequired}
            error={form.formState.errors.lotNo?.message}
            labelRight={
              manualLot ? (
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  icon={<ChevronLeft className="size-4" aria-hidden />}
                  iconPosition="leading"
                  onClick={() => setManualLot(false)}
                >
                  Back to list
                </Button>
              ) : undefined
            }
            hint={manualLot ? 'Off-system entry — type the lot from the vial' : 'Select the lot printed on the vial'}
          >
            {manualLot ? (
              <Controller
                name="lotNo"
                control={form.control}
                render={({ field }) => (
                  <TextField size="compact"
                    type="text"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="Type lot from vial"
                    maxLength={100}
                  />
                )}
              />
            ) : (
              <Controller
                name="lotNo"
                control={form.control}
                render={({ field }) => (
                  <LotPicker
                    value={field.value}
                    lots={lots}
                    isLoading={lotsQ.isLoading}
                    isError={lotsQ.isError}
                    onRetry={() => lotsQ.refetch()}
                    onSelect={(lot) => {
                      field.onChange(lot.batchNumber);
                      // Auto-fill expiry from the selected lot.
                      form.setValue('expiryDisplay', toYmd(lot.expiryDate), { shouldValidate: true });
                    }}
                    onEnterManually={() => {
                      setManualLot(true);
                      field.onChange('');
                      form.setValue('expiryDisplay', '');
                    }}
                  />
                )}
              />
            )}
          </Field>
        </div>
        )}

        {/* off-system caution banner */}
        {manualLot && !authoring && designV2 && (
          // Design 21-10 (EncVaccineForm): the DS warning Banner, titled, the line under it.
          <Banner variant="warning" title="Not in recorded stock">
            {form.watch('lotNo') || 'This lot'} isn’t on hand for {vaccine.valueCoding.display} at the default store.
            The dose will still be recorded — it’ll be flagged for stock reconciliation, not deducted from another lot.
          </Banner>
        )}
        {manualLot && !authoring && !designV2 && (
          <div className="flex items-start gap-3 rounded-md bg-yellow-100 px-3 py-2.5 text-sm text-status-amber-text">
            <AlertTriangle className="size-4 mt-1 shrink-0" aria-hidden />
            <p>
              <span className="font-semibold">Not in recorded stock.</span>{' '}
              {form.watch('lotNo') || 'This lot'} isn’t on hand for {vaccine.valueCoding.display} at the default store.
              The dose will still be recorded — it’ll be flagged for stock reconciliation, not deducted from another lot.
            </p>
          </div>
        )}

        {/* Remarks — last (Flutter) */}
        {!authoring && (
          <Field label="Remarks">
            <TextArea {...form.register('remarks')} placeholder="Optional remarks…" rows={3} maxLength={300} />
          </Field>
        )}

        {saveError && designV2 && (
          <Banner variant="error" role="alert" title="Not saved">
            {saveError}
          </Banner>
        )}
        {saveError && !designV2 && (
          <p role="alert" className="text-sm text-status-error">
            {saveError}
          </p>
        )}

        {/* Save + Cancel left, destructive delete far right — the footer both Figma order-dialog
            frames draw, and the layout OrderDialogShell/the med dialog use. */}
        {/* FIXED FOOTER — Flutter keeps the actions outside the scrolling body
            (base_ap_item_add_widget.dart:88), so a long form never hides Save. */}
        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-2 border-t border-border-subtle bg-surface-card pt-3 pb-1">
          <div className="flex items-center gap-2">
            <Button type="submit" loading={saving} disabled={saving}>
              {saving ? 'Saving…' : saveLabel ?? 'Save'}
            </Button>
            <Button type="button" variant="tertiary" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          </div>
          {onDelete && (
            <button
              type="button"
              aria-label="Delete vaccination"
              // The app icon button: a destructive icon is neutral, like every row bin (icon pass, both modes).
              className="icon-action"
              onClick={onDelete}
              disabled={saving}
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// ── small field wrapper ──────────────────────────────────────────────────────────────────────────
function Field({
  label,
  required,
  error,
  hint,
  labelRight,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  labelRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      {/*
        FIXED-HEIGHT LABEL ROW. Andrew, 1 Aug: "the lot number should be always in the same line with
        expiration date." The cause was not the control heights (those are all 36 now) — it was THIS
        row. LOT NO. carries a "Back to list" link in `labelRight`, and a link is taller than a 12px
        uppercase label, so that one row grew and pushed its field ~20px below the Expiration-date
        field beside it.

        h-5 pins every label row to the same height whether or not it has a right-hand action, so
        paired fields always start on the same line. `overflow-visible` keeps the link from clipping.
      */}
      <div className="mb-1 flex h-5 items-center justify-between overflow-visible">
        <label className="text-xs font-bold uppercase tracking-wide text-text-muted" data-part="field-label">
          {label} {required ? <span className="text-red-700" data-part="required">*</span> : null}
        </label>
        {labelRight}
      </div>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-700" role="alert" data-part="field-error">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-text-muted" data-part="hint">{hint}</p>
      ) : null}
    </div>
  );
}
